import { FormattedMessage } from 'react-intl';
import { Button, Input, InputNumber, Spin } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';

import { CashOutBrandPicker } from './cash-out-brand-picker';
import { CashOutHistory } from './cash-out-history';
import { CashOutStatus } from './cash-out-status';
import {
  CODE_LENGTH,
  FAILED_STATUSES,
  STEP_INDEX,
  STEP_LABELS,
  TERMINAL_STATUSES,
  useWalletCashOut,
} from './use-wallet-cash-out';

import './style.scss';

export function WalletCashOut() {
  const {
    intl,
    userWalletData,
    walletCode,
    isLoading,
    options,
    history,
    step,
    setStep,
    amount,
    productId,
    setProductId,
    quote,
    detail,
    isBusy,
    isRedirecting,
    challenge,
    code,
    setCode,
    resendIn,
    resultRef,
    formatPoints,
    formatMoney,
    brandsForAmount,
    selectedBrand,
    selectAmount,
    amountError,
    feeHint,
    amountBreakdown,
    openDetail,
    handleContinue,
    sendCode,
    handleConfirm,
    handleRedeem,
    handleStartOver,
    quickAmounts,
  } = useWalletCashOut();

  if (isLoading) {
    return (
      <div className="wallet-cash-out wallet-cash-out--loading">
        <Spin />
      </div>
    );
  }

  if (!walletCode || !options) {
    return (
      <div className="wallet-cash-out">
        <div className="wallet-cash-out__card wallet-cash-out__notice">
          <FormattedMessage id="wallet.cashout.unavailable" />
        </div>
      </div>
    );
  }

  const currentStep = STEP_INDEX[step];
  const brandName = (id?: string | null) => options.brands.find((b) => b.productId === id)?.name ?? id;

  return (
    <div className="wallet-cash-out">
      {isRedirecting ? (
        <div className="wallet-cash-out__redirect" role="status" aria-live="polite">
          <div className="wallet-cash-out__redirect-card">
            <div className="wallet-cash-out__redirect-spinner" />
            <p className="wallet-cash-out__redirect-title">
              <FormattedMessage id="wallet.cashout.redirect.title" />
            </p>
            <p className="wallet-cash-out__hint">
              <FormattedMessage id="wallet.cashout.redirect.hint" />
            </p>
          </div>
        </div>
      ) : null}

      <header className="wallet-cash-out__header">
        <h1 className="wallet-cash-out__title">
          <FormattedMessage id="wallet.cashout.title" />
        </h1>
        <p className="wallet-cash-out__subtitle">
          <FormattedMessage id="wallet.cashout.entry.hint" />
        </p>
      </header>

      <section className="wallet-cash-out__balance-card">
        <div className="wallet-cash-out__balance-main">
          <span className="wallet-cash-out__eyebrow">
            <FormattedMessage id="wallet.cashout.balance.cashable" />
          </span>
          <span className="wallet-cash-out__balance-amount">
            {options.eligible ? formatPoints(options.cashableBalance) : formatPoints(0)}
          </span>
        </div>
        <div className="wallet-cash-out__balance-side">
          <div className="wallet-cash-out__balance-item">
            <span className="wallet-cash-out__balance-label">
              <FormattedMessage id="wallet.cashout.balance.available" />
            </span>
            <span className="wallet-cash-out__balance-value">{formatPoints(userWalletData?.balance ?? 0)}</span>
          </div>
          {options.commissionBalance > 0 ? (
            <div className="wallet-cash-out__balance-item">
              <span className="wallet-cash-out__balance-label">
                <FormattedMessage id="wallet.cashout.balance.commission" />
              </span>
              <span className="wallet-cash-out__balance-value">
                {formatMoney(options.commissionBalance, options.currencyCode)}
              </span>
            </div>
          ) : null}
        </div>
        <p className="wallet-cash-out__balance-hint">
          <FormattedMessage
            id={
              options.commissionBalance > 0
                ? 'wallet.cashout.balance.cashable.hint.commission'
                : 'wallet.cashout.balance.cashable.hint'
            }
          />
        </p>
      </section>

      {!options.eligible ? (
        <section className="wallet-cash-out__card wallet-cash-out__notice">
          <p className="wallet-cash-out__notice-title">
            <FormattedMessage id="wallet.cashout.not_eligible" />
          </p>
          <p className="wallet-cash-out__hint">
            {options.reason ?? <FormattedMessage id="wallet.cashout.not_eligible.hint" />}
          </p>
        </section>
      ) : (
        <ol className="wallet-cash-out__steps">
          {STEP_LABELS.map((labelId, index) => (
            <li
              key={labelId}
              className={`wallet-cash-out__step-dot ${index === currentStep ? 'is-current' : ''} ${index < currentStep ? 'is-done' : ''}`}
            >
              <span className="wallet-cash-out__step-number">{index + 1}</span>
              <span className="wallet-cash-out__step-label">
                <FormattedMessage id={labelId} />
              </span>
            </li>
          ))}
        </ol>
      )}

      {options.eligible && step === 'form' ? (
        <>
          <section className="wallet-cash-out__card">
            <h2 className="wallet-cash-out__card-title">
              <FormattedMessage id="wallet.cashout.amount.label" />
            </h2>
            {quickAmounts.length ? (
              <div className="wallet-cash-out__chips">
                {quickAmounts.map(({ value, isAll }) => (
                  <button
                    key={value}
                    type="button"
                    className={`wallet-cash-out__chip ${amount === value ? 'is-selected' : ''}`}
                    onClick={() => selectAmount(value)}
                  >
                    {isAll
                      ? intl.formatMessage({ id: 'wallet.cashout.amount.all' }, { amount: formatMoney(value, options.currencyCode) })
                      : formatMoney(value, options.currencyCode)}
                  </button>
                ))}
              </div>
            ) : null}
            <InputNumber
              id="wallet-cash-out-amount"
              className="wallet-cash-out__amount"
              size="large"
              min={0}
              precision={2}
              prefix="$"
              placeholder={intl.formatMessage({ id: 'wallet.cashout.amount.placeholder' })}
              value={amount ?? undefined}
              onChange={(value) => selectAmount(value == null ? null : Number(value))}
            />
            {amountError ? <p className="wallet-cash-out__error">{amountError}</p> : null}
            {amountBreakdown ? <p className="wallet-cash-out__breakdown">{amountBreakdown}</p> : null}
            <p className="wallet-cash-out__hint">
              {options.minAmount != null ? (
                <>
                  <FormattedMessage
                    id="wallet.cashout.amount.min_hint"
                    values={{ min: formatMoney(options.minAmount, options.currencyCode) }}
                  />{' '}
                </>
              ) : null}
              {amountBreakdown ? null : feeHint}
            </p>
          </section>

          {amount != null && !amountError ? (
            <section className="wallet-cash-out__card">
              <h2 className="wallet-cash-out__card-title">
                <FormattedMessage id="wallet.cashout.brand.label" />
              </h2>
              {brandsForAmount.length ? (
                <CashOutBrandPicker brands={brandsForAmount} selectedId={productId} onSelect={setProductId} />
              ) : (
                <p className="wallet-cash-out__hint">
                  <FormattedMessage id="wallet.cashout.brand.none" />
                </p>
              )}
            </section>
          ) : null}

          <div className="wallet-cash-out__actions">
            <Button
              type="primary"
              size="large"
              disabled={amount == null || !productId || !!amountError}
              loading={isBusy}
              onClick={handleContinue}
            >
              <FormattedMessage id="wallet.cashout.continue" />
            </Button>
          </div>
        </>
      ) : null}

      {step === 'confirm' && quote ? (
        <section className="wallet-cash-out__card">
          <h2 className="wallet-cash-out__card-title">
            <FormattedMessage id="wallet.cashout.confirm.title" />
          </h2>
          <div className="wallet-cash-out__review">
            {selectedBrand?.imageUrl ? (
              <img className="wallet-cash-out__review-image" src={selectedBrand.imageUrl} alt="" />
            ) : null}
            <dl className="wallet-cash-out__summary">
              <div>
                <dt><FormattedMessage id="wallet.cashout.confirm.brand" /></dt>
                <dd>{selectedBrand?.name ?? productId}</dd>
              </div>
              <div>
                <dt><FormattedMessage id="wallet.cashout.confirm.debited" /></dt>
                <dd>{formatPoints(quote.pointsDebited)}</dd>
              </div>
              <div>
                <dt><FormattedMessage id="wallet.cashout.confirm.fee" /></dt>
                <dd>{quote.feeAmount > 0 ? `− ${formatPoints(quote.feeAmount)}` : formatPoints(0)}</dd>
              </div>
              <div className="wallet-cash-out__summary-total">
                <dt><FormattedMessage id="wallet.cashout.confirm.gift_card" /></dt>
                <dd>{formatMoney(quote.payoutAmount, options.currencyCode)}</dd>
              </div>
            </dl>
          </div>
          <p className="wallet-cash-out__hint">
            <FormattedMessage
              id={quote.feeMode === 'Gross' ? 'wallet.cashout.confirm.gross' : 'wallet.cashout.confirm.net'}
              values={{
                debited: formatPoints(quote.pointsDebited),
                giftCard: formatMoney(quote.payoutAmount, options.currencyCode),
              }}
            />
          </p>
          {options.requiresVerification ? (
            <p className="wallet-cash-out__hint">
              <FormattedMessage id="wallet.cashout.verify.hint" />
            </p>
          ) : null}
          <div className="wallet-cash-out__warning" role="note">
            <FontAwesomeIcon className="wallet-cash-out__warning-icon" icon={faTriangleExclamation} />
            <div>
              <p className="wallet-cash-out__warning-title">
                <FormattedMessage id="wallet.cashout.confirm.final.title" />
              </p>
              <p className="wallet-cash-out__warning-text">
                <FormattedMessage id="wallet.cashout.confirm.final.text" />
              </p>
            </div>
          </div>
          <div className="wallet-cash-out__actions">
            <Button size="large" disabled={isBusy} onClick={() => setStep('form')}>
              <FormattedMessage id="wallet.cashout.back" />
            </Button>
            {options.requiresVerification ? (
              <Button type="primary" size="large" loading={isBusy} onClick={sendCode}>
                <FormattedMessage id="wallet.cashout.verify.send" />
              </Button>
            ) : (
              <Button type="primary" size="large" loading={isBusy} onClick={handleConfirm}>
                <FormattedMessage id="wallet.cashout.confirm.submit" />
              </Button>
            )}
          </div>
        </section>
      ) : null}

      {step === 'verify' && challenge ? (
        <section className="wallet-cash-out__card">
          <h2 className="wallet-cash-out__card-title">
            <FormattedMessage id="wallet.cashout.verify.title" />
          </h2>
          <p className="wallet-cash-out__hint">
            <FormattedMessage id="wallet.cashout.verify.sent" values={{ email: challenge.sentTo ?? '' }} />
          </p>
          <Input
            className="wallet-cash-out__code"
            size="large"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={CODE_LENGTH}
            value={code}
            onChange={(event) => setCode(event.target.value.replace(/\D/g, '').slice(0, CODE_LENGTH))}
            onPressEnter={handleConfirm}
          />
          <div className="wallet-cash-out__actions">
            <Button size="large" disabled={isBusy || resendIn > 0} onClick={sendCode}>
              {resendIn > 0
                ? intl.formatMessage({ id: 'wallet.cashout.verify.resend_in' }, { seconds: resendIn })
                : intl.formatMessage({ id: 'wallet.cashout.verify.resend' })}
            </Button>
            <Button type="primary" size="large" disabled={code.length !== CODE_LENGTH} loading={isBusy} onClick={handleConfirm}>
              <FormattedMessage id="wallet.cashout.confirm.submit" />
            </Button>
          </div>
        </section>
      ) : null}

      {step === 'result' && detail ? (
        <section className="wallet-cash-out__card wallet-cash-out__result" ref={resultRef}>
          <div className="wallet-cash-out__result-head">
            <h2 className="wallet-cash-out__card-title">
              <FormattedMessage id="wallet.cashout.result.title" />
            </h2>
            <CashOutStatus status={detail.status} />
          </div>

          <p className="wallet-cash-out__result-amount">
            {formatMoney(detail.payoutAmount, detail.currencyCode)}
          </p>
          <p className="wallet-cash-out__result-brand">{brandName(detail.productId)}</p>

          {detail.redemptionLink ? (
            <>
              <button
                type="button"
                className="wallet-cash-out__redeem"
                onClick={() => handleRedeem(detail.redemptionLink as string)}
              >
                <FormattedMessage id="wallet.cashout.result.redeem" />
              </button>
              <p className="wallet-cash-out__hint">
                <FormattedMessage id="wallet.cashout.result.redeem.hint" />
              </p>
            </>
          ) : null}

          {!TERMINAL_STATUSES.includes(detail.status) ? (
            <p className="wallet-cash-out__hint">
              <FormattedMessage id="wallet.cashout.result.processing" />
            </p>
          ) : null}

          {FAILED_STATUSES.includes(detail.status) ? (
            <p className="wallet-cash-out__hint">
              <FormattedMessage id="wallet.cashout.result.failed" />
            </p>
          ) : null}

          <div className="wallet-cash-out__actions">
            {!TERMINAL_STATUSES.includes(detail.status) ? (
              <Button size="large" loading={isBusy} onClick={() => openDetail(detail.cashOutCode)}>
                <FormattedMessage id="wallet.cashout.result.refresh" />
              </Button>
            ) : null}
            {options.eligible ? (
              <Button type="primary" size="large" disabled={isBusy} onClick={handleStartOver}>
                <FormattedMessage id="wallet.cashout.result.new" />
              </Button>
            ) : null}
          </div>
        </section>
      ) : null}

      <CashOutHistory
        cashOuts={history}
        brands={options.brands}
        formatPoints={formatPoints}
        formatMoney={formatMoney}
        onView={openDetail}
      />
    </div>
  );
}
